import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useWorkflowDefaults, WorkflowItem } from "@/hooks/useWorkflowDefaults";
import {
  useRequiredSpareParts,
  useWorkflowStepParts,
  sumPartsPrice,
  sumBindingsPrice,
} from "@/hooks/useSparePartsCost";

const categoryToApplianceType: Record<string, string> = {
  "washing-machine": "Washing Machine",
  "washing machine": "Washing Machine",
  "dryer": "Dryer",
  "tumble dryer": "Dryer",
  "dishwasher": "Dishwasher",
  "oven": "Oven",
  "refrigerator": "Household Refrigerator",
  "household refrigerator": "Household Refrigerator",
  "vacuum-cleaner": "Vacuum Cleaner",
};

const normalizeApplianceType = (category: string): string => {
  const lower = (category || "").toLowerCase();
  return categoryToApplianceType[lower] || category;
};

const normalizeBrand = (brand: string): string => {
  const b = (brand || "").trim();
  if (!b) return b;
  const upper = b.toUpperCase();
  // BSH is the manufacturing group (Bosch-Siemens-Hausgeräte); spare parts catalog uses commercial brand "Bosch"
  if (upper === "BSH") return "Bosch";
  if (upper === "AEG") return "AEG";
  if (upper === "LG") return "LG";
  return b.charAt(0).toUpperCase() + b.slice(1).toLowerCase();
};

// Phase categories included in the refurbish workflow process UI
// (mirrors PHASE_CONFIG in RefurbishmentProcess; error_codes is included as a single phase step)
const REFURBISH_PHASE_CATEGORIES = [
  "refurbish_start",
  "error_codes",
  "troubleshooting",
  "parts_replacement",
  "verification",
  "cleaning",
] as const;

// Missing values in workflow management count as 0 — no per-item or per-phase fallbacks.
// (Final "Quality & Completion" steps are hardcoded outside Workflow Management and keep their times.)

// Final hardcoded "Quality & Completion" steps appended in RefurbishmentProcess (15 + 10 = 25)
const FINAL_STEPS_MINUTES = 15 + 10;

interface UseEstimateResult {
  totalMinutes: number;
  laborCost: number;
  materialCost: number;
  totalCost: number;
  /** @deprecated use `totalCost` — kept for backward compatibility (= laborCost + materialCost) */
  estimatedCost: number;
  stepCount: number;
  isLoading: boolean;
}

/**
 * Resolves per-item minutes from Workflow Management:
 *   - brand-specific override > item value
 *   - if value > 0  -> that value
 *   - if value === 0, null, or missing -> 0 (no time)
 */
const resolveItemMinutes = (
  itemId: string,
  itemEstimatedMinutes: number | null | undefined,
  minutesByItem: Map<string, number>,
): number => {
  const override = minutesByItem.get(itemId);
  const raw = override ?? (itemEstimatedMinutes != null ? Number(itemEstimatedMinutes) : null);
  if (raw != null && Number.isFinite(raw) && raw > 0) return raw;
  return 0;
};

/**
 * Computes total time + cost estimate for refurbishment of a specific appliance,
 * using the steps defined for its brand in Workflow Management plus the per-phase
 * roles + their hourly rates, plus required spare parts and parts bound to
 * the active workflow steps.
 *
 * Mirrors the calculation in RefurbishmentProcess.tsx + StepsOverviewDialog.tsx so the
 * Professional Assessment total matches the Process Overview total.
 */
export function useRefurbishmentEstimate(
  brand: string | null,
  category: string | null,
  selectedErrorCodeIds: string[] = [],
  modelNumber: string | null = null,
): UseEstimateResult {
  const { defaults, isLoading: loadingDefaults } = useWorkflowDefaults(brand, category, "refurbish");

  const [dynamicItems, setDynamicItems] = useState<WorkflowItem[]>([]);
  const [minutesByItem, setMinutesByItem] = useState<Map<string, number>>(new Map());
  const [hourlyRateByPhase, setHourlyRateByPhase] = useState<Map<string, number>>(new Map());
  const [phaseFlags, setPhaseFlags] = useState<Map<string, { enabled: boolean; minutes: number | null }>>(new Map());
  const [isLoading, setIsLoading] = useState(false);

  const applianceType = category ? normalizeApplianceType(category) : "";
  const normalizedBrand = brand ? normalizeBrand(brand) : "";

  // Fetch dynamic items added by selected error codes
  useEffect(() => {
    if (!selectedErrorCodeIds || selectedErrorCodeIds.length === 0) {
      setDynamicItems([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data: mappings } = await supabase
        .from("workflow_item_mappings")
        .select("target_item_id")
        .in("source_item_id", selectedErrorCodeIds);
      if (!mappings || mappings.length === 0) {
        if (!cancelled) setDynamicItems([]);
        return;
      }
      const targetIds = [...new Set(mappings.map((m) => m.target_item_id))];
      const { data: items } = await supabase
        .from("workflow_items")
        .select("*")
        .in("id", targetIds);
      if (!cancelled) setDynamicItems((items || []) as WorkflowItem[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedErrorCodeIds.join("|")]);

  // Fetch estimated_minutes overrides + phase roles + custom_roles + phase defaults
  useEffect(() => {
    if (!applianceType || !normalizedBrand) return;
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const [defsRes, parentDefsRes, phaseRolesRes, customRolesRes, phaseParentRes, phaseBrandRes] = await Promise.all([
          supabase
            .from("workflow_defaults")
            .select("workflow_item_id, estimated_minutes")
            .eq("appliance_type", applianceType)
            .eq("brand", normalizedBrand),
          supabase
            .from("workflow_defaults")
            .select("workflow_item_id, estimated_minutes")
            .eq("appliance_type", applianceType)
            .is("brand", null),
          supabase
            .from("workflow_phase_roles")
            .select("phase_category, role_type, role_value")
            .eq("appliance_type", applianceType),
          supabase.from("custom_roles").select("id, hourly_rate"),
          supabase
            .from("workflow_phase_defaults")
            .select("phase_category, for_refurbish, estimated_minutes")
            .eq("appliance_type", applianceType)
            .is("brand", null),
          supabase
            .from("workflow_phase_defaults")
            .select("phase_category, for_refurbish, estimated_minutes")
            .eq("appliance_type", applianceType)
            .eq("brand", normalizedBrand),
        ]);

        const minutesMap = new Map<string, number>();
        for (const row of parentDefsRes.data || []) {
          if (row.estimated_minutes != null) minutesMap.set(row.workflow_item_id, Number(row.estimated_minutes));
        }
        for (const row of defsRes.data || []) {
          if (row.estimated_minutes != null) minutesMap.set(row.workflow_item_id, Number(row.estimated_minutes));
        }

        const rateById = new Map<string, number>();
        for (const r of customRolesRes.data || []) {
          rateById.set(r.id as string, Number(r.hourly_rate || 0));
        }
        const ratesByPhase = new Map<string, number[]>();
        for (const pr of phaseRolesRes.data || []) {
          if (pr.role_type !== "custom") continue;
          const rate = rateById.get(pr.role_value);
          if (rate == null) continue;
          const arr = ratesByPhase.get(pr.phase_category) || [];
          arr.push(rate);
          ratesByPhase.set(pr.phase_category, arr);
        }
        const hourlyByPhase = new Map<string, number>();
        for (const [phase, rates] of ratesByPhase) {
          if (rates.length === 0) continue;
          hourlyByPhase.set(phase, rates.reduce((a, b) => a + b, 0) / rates.length);
        }

        // Merge phase defaults: brand overrides parent
        const flagsMap = new Map<string, { enabled: boolean; minutes: number | null }>();
        for (const row of phaseParentRes.data || []) {
          flagsMap.set(row.phase_category, {
            enabled: row.for_refurbish !== false,
            minutes: row.estimated_minutes != null ? Number(row.estimated_minutes) : null,
          });
        }
        for (const row of phaseBrandRes.data || []) {
          const existing = flagsMap.get(row.phase_category);
          flagsMap.set(row.phase_category, {
            enabled: row.for_refurbish !== false,
            minutes:
              row.estimated_minutes != null
                ? Number(row.estimated_minutes)
                : existing?.minutes ?? null,
          });
        }

        if (!cancelled) {
          setMinutesByItem(minutesMap);
          setHourlyRateByPhase(hourlyByPhase);
          setPhaseFlags(flagsMap);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applianceType, normalizedBrand]);

  // Helpers mirroring usePhaseDefaults
  const isPhaseEnabled = (phaseCategory: string): boolean => {
    const f = phaseFlags.get(phaseCategory);
    return f ? f.enabled : true;
  };
  const getPhaseMinutes = (phaseCategory: string): number | null => {
    const f = phaseFlags.get(phaseCategory);
    return f ? f.minutes : null;
  };

  // Aggregate
  const allItems: { item: WorkflowItem; phase: string }[] = [];
  const phaseMap: Record<string, string> = {
    refurbishStart: "refurbish_start",
    troubleshooting: "troubleshooting",
    partsReplacement: "parts_replacement",
    verification: "verification",
    cleaning: "cleaning",
  };
  for (const key of Object.keys(phaseMap) as (keyof typeof defaults)[]) {
    const phase = phaseMap[key as string];
    if (!REFURBISH_PHASE_CATEGORIES.includes(phase as typeof REFURBISH_PHASE_CATEGORIES[number])) continue;
    if (!isPhaseEnabled(phase)) continue;
    for (const item of defaults[key] || []) {
      allItems.push({ item, phase });
    }
  }
  // Add dynamic items from error code mappings (avoid duplicates)
  const existingIds = new Set(allItems.map((x) => x.item.id));
  for (const item of dynamicItems) {
    if (existingIds.has(item.id)) continue;
    if (item.category === "troubleshooting" && isPhaseEnabled("troubleshooting"))
      allItems.push({ item, phase: "troubleshooting" });
    else if (item.category === "parts_replacement" && isPhaseEnabled("parts_replacement"))
      allItems.push({ item, phase: "parts_replacement" });
  }

  let totalMinutes = 0;
  let laborCost = 0;
  for (const { item, phase } of allItems) {
    const itemAny = item as WorkflowItem & { estimated_minutes?: number | null };
    const minutes = resolveItemMinutes(item.id, itemAny.estimated_minutes, minutesByItem);
    totalMinutes += minutes;
    const hourly = hourlyRateByPhase.get(phase) || 0;
    laborCost += (minutes / 60) * hourly;
  }

  // Error Codes phase: rendered as a single step using phase-level minutes from
  // Workflow Management. Missing or 0 → contributes 0 (no time shown).
  let errorCodeStepCount = 0;
  if (isPhaseEnabled("error_codes")) {
    const phaseMin = getPhaseMinutes("error_codes");
    const ecMinutes = phaseMin != null && phaseMin > 0 ? phaseMin : 0;
    totalMinutes += ecMinutes;
    const hourly = hourlyRateByPhase.get("error_codes") || 0;
    laborCost += (ecMinutes / 60) * hourly;
    errorCodeStepCount = 1;
  }

  // Final "Quality & Completion" hardcoded steps (always added in RefurbishmentProcess).
  totalMinutes += FINAL_STEPS_MINUTES;

  // Material cost = required parts (for this appliance) + parts bound to active workflow steps
  const activeItemIds = allItems.map((x) => x.item.id);
  const { data: requiredParts = [] } = useRequiredSpareParts(applianceType, normalizedBrand, modelNumber);
  const { data: stepParts = [] } = useWorkflowStepParts(activeItemIds, normalizedBrand, modelNumber);
  const materialCost = sumPartsPrice(requiredParts) + sumBindingsPrice(stepParts);
  const totalCost = laborCost + materialCost;
  if (typeof window !== "undefined") {
    console.log("[useRefurbishmentEstimate]", {
      applianceType,
      normalizedBrand,
      modelNumber,
      totalMinutes,
      requiredPartsCount: requiredParts.length,
      stepPartsCount: stepParts.length,
      materialCost,
      laborCost,
    });
  }

  return {
    totalMinutes,
    laborCost,
    materialCost,
    totalCost,
    estimatedCost: totalCost,
    stepCount: allItems.length + errorCodeStepCount + 2, // +2 for FINAL_STEPS
    isLoading: isLoading || loadingDefaults,
  };
}

export function formatMinutes(totalMinutes: number): string {
  if (!totalMinutes || totalMinutes <= 0) return "—";
  const h = Math.floor(totalMinutes / 60);
  const m = Math.round(totalMinutes % 60);
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}
